import { Button } from "@workspace/ui/components/button";
import { Logo } from "@/components/ui/icons/logo";
import { cn } from "@workspace/ui/lib/utils";
import { useMediaQuery } from "@/hooks/use-media-query";

interface UsedProductsExplainerProps {
  title: string;
  columns: ComparisonColumn[];
  rows: Array<{ value: string; background: string }[]>;
  handleNext: () => void;
}

interface ComparisonColumn {
  type: string;
  text: string;
  image?: string;
  background?: string;
}

export default function UsedProductsExplainer({
  title,
  columns,
  rows,
  handleNext,
}: UsedProductsExplainerProps) {
  const isThreeColumns = columns.length === 3;
  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <>
      <div className="flex flex-col gap-6 flex-1 overflow-y-auto min-h-0 pb-12">
        <h2 className="text-primary title-16 md:title-32"
        id="question-title"
        data-testid="question-title"
        >
          {title}
        </h2>
        <div>
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
            }}
          >
            {columns.map((column, index) => (
              <div
                key={column.text}
                className={cn(
                  "min-w-0 p-4 flex border-t border-b border-[#EBEBEB] items-center",
                  index === 0 && "border-l rounded-tl-2xl",
                  index === columns.length - 1 &&
                    "border-r rounded-tr-2xl justify-center"
                )}
                style={{ background: column.background }}
              >
                {column.type === "logo" ? (
                  <div className="min-w-0 flex gap-1 items-center w-full max-w-full overflow-hidden">
                    <Logo
                      className={cn(
                        "flex-shrink-0",
                        isThreeColumns ? "w-10 h-4" : "w-16 h-6",
                        "md:w-16 md:h-6"
                      )}
                    />
                    <div
                      className="flex-shrink-0"
                      style={isMobile ? {
                        width: '100px',
                        height: '70px',
                        flexShrink: 0,
                        aspectRatio: '100/60',
                        marginLeft: isThreeColumns ? '-8px' : '-30px',
                        background: `url(${column.image!}) center / contain no-repeat`,
                      } : {
                        width: '52.788px',
                        height: '36px',
                        flexShrink: 0,
                        aspectRatio: '52.79/36.00',
                        background: `url(${column.image!}) -1.926px -8.618px / 107.297% 146.633% no-repeat`,
                      }}
                    />
                  </div>
                ) : (
                  <p className="min-w-0 title-14 text-primary text-center break-words">
                    {column.text}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${rows.length}, 1fr)`,
            }}
          >
            {rows.map((row, rowIndex) =>
              row.map((item, itemIndex) => (
                <div
                  key={`${rowIndex}-${itemIndex}`}
                  className={cn(
                    "min-w-0 flex items-center p-4 border-b border-[#EBEBEB]",
                    itemIndex === 0 && "border-l",
                    itemIndex === row.length - 1 && "border-r",
                    rowIndex === rows.length - 1 &&
                      itemIndex === 0 &&
                      "rounded-bl-2xl",
                    rowIndex === rows.length - 1 &&
                      itemIndex === row.length - 1 &&
                      "rounded-br-2xl"
                  )}
                  style={{ background: item.background }}
                >
                  <p
                    className={cn(
                      "min-w-0 captitle-14 normal-case break-words",
                      itemIndex === 0 && "text-primary",
                      itemIndex === row.length - 1 && "text-muted-foreground"
                    )}
                  >
                    {item.value}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <div className="sticky bottom-0 flex flex-col gap-3 pb-6">
        <Button onClick={handleNext} variant="default" className="w-full shrink-0">See my hormone chart</Button>
      </div>
    </>
  );
}
